import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './BlogPage.css';

// Artigos semanais: um ficheiro .json por artigo na pasta src/artigos.
// Para publicar um artigo novo basta carregar o ficheiro nessa pasta — não é preciso mexer neste ficheiro.
// O nome do ficheiro começa pela data (AAAA-MM-DD), por isso o mais recente aparece primeiro.
const pastaArtigos = require.context('../artigos', false, /\.json$/);
const ARTIGOS_SEMANAIS = pastaArtigos.keys().sort().reverse().map((k) => pastaArtigos(k));

const ARTIGOS_BASE = [
  {
    slug: 'garantia-publica-jovens-2027-credito-habitacao',
    titulo: 'Garantia Pública em 2027: O Crédito a 100% Para Jovens Vai Continuar?',
    resumo: 'O relatório do Orçamento do Estado para 2027 diz que a garantia pública para a primeira casa "manter-se-á". Mas há um detalhe que poucos estão a explicar — e que pode mudar a sua decisão de compra nos próximos meses.',
    data: '9 de Outubro de 2026',
    leitura: '6 min',
    categoria: 'Comprar',
    cor: '#051d40',
    conteudo: `
## O que é a garantia pública, em 30 segundos

Normalmente, os bancos financiam até 90% do valor da casa. Os outros 10% — a entrada — saem do bolso do comprador. Numa casa de 250.000€, são 25.000€.

Com a garantia pública, **o Estado fica como fiador de até 15% do empréstimo**, durante 10 anos. Isso permite ao banco financiar **até 100%** do valor do imóvel.

Não é um subsídio nem um desconto: deve o valor total e paga juros sobre ele. Mas elimina a maior barreira de quem quer comprar casa jovem — juntar a entrada.

## O que diz o OE2027 — e o que ainda não diz

O relatório da proposta de Orçamento para 2027 afirma que "manter-se-á a garantia pública na compra da primeira habitação", a par das isenções de IMT e Imposto do Selo.

**O detalhe que importa:** a proposta de Orçamento, por si só, não prolonga a garantia. As regras actuais só abrangem contratos de crédito **assinados até 31 de dezembro de 2026**. A continuação em 2027 depende de um diploma próprio, ainda não aprovado. O próprio ministro das Finanças, Joaquim Miranda Sarmento, disse que a experiência "tem sido positiva", mas que "a decisão final ainda não foi tomada formalmente".

Traduzindo: **a intenção política existe, mas a regra ainda não está publicada.** Não dou isto como garantido — e não aconselho nenhum cliente a fazê-lo.

## Os números que mostram porque é provável continuar

- **Quase 120 mil jovens** já beneficiaram das medidas de apoio à primeira casa (garantia e isenções), com um valor médio de compra de cerca de 200 mil euros
- As responsabilidades do Estado com estas garantias passaram de 449 milhões de euros no final de 2025 para **cerca de 926 milhões em maio de 2026**
- O Governo estima um **risco de incumprimento reduzido**, com base no histórico

Uma medida com esta adesão e baixo risco dificilmente desaparece de um dia para o outro. Mas "dificilmente" não é "certamente".

## Quem pode usar a garantia (regras actuais)

→ Ter entre 18 e 35 anos

→ Comprar a primeira habitação própria e permanente

→ Imóvel até 450.000€ (conta o menor valor entre o preço e a avaliação bancária)

→ Rendimento colectável até ao 8.º escalão de IRS (86.634€ em 2026; em casal conta o quociente conjugal)

→ Domicílio fiscal em Portugal

→ Nunca ter sido proprietário de habitação nem ter usado antes esta garantia

→ Sem dívidas às Finanças ou à Segurança Social

E o ponto que muitos esquecem: **a decisão final é sempre do banco**. A garantia resolve a entrada, não resolve a taxa de esforço. O Banco de Portugal recomenda uma taxa de esforço máxima de 45%, testada com uma subida de 1,5 pontos na taxa de juro.

## Um exemplo real: T2 de 250.000€ em Vila Franca de Xira

Um jovem de 30 anos compra um T2 de 250.000€ e junta as duas medidas.

**Sem apoios:**

- Entrada (10%): 25.000€
- IMT + Imposto do Selo: cerca de 8.800€
- **Dinheiro necessário à cabeça: cerca de 33.800€ + despesas**

**Com garantia pública + IMT Jovem:**

- Entrada: 0€
- IMT + Imposto do Selo: 0€
- **Dinheiro necessário à cabeça: apenas despesas (cerca de 1.500€ a 3.000€)**

A diferença é enorme. Mas há um custo: financiar 100% em vez de 90% aumenta a prestação e os juros totais. Numa casa de 200.000€, com crédito a 40 anos e taxa de 3,5%, a prestação passa de cerca de 697€ (com 10% de entrada) para cerca de 775€ (a 100%) — e os juros totais sobem cerca de 17.000€.

**A garantia é uma ferramenta, não uma obrigação.** Se tiver parte da entrada, use-a. Se não tiver, a garantia permite comprar agora em vez de pagar renda mais cinco anos.

## O que fazer já, se está a pensar comprar

**1. Não espere pela confirmação para se preparar.** Peça já uma pré-aprovação. Saber o seu limite real de crédito leva dias e é o que separa quem compra de quem perde a casa para outro comprador.

**2. Se o imóvel já está escolhido, aponte para escriturar até 31 de dezembro de 2026.** É a única data com cobertura certa neste momento.

**3. Confirme se cumpre os requisitos das duas medidas** — garantia e IMT Jovem. Os limites de valor são diferentes (450.000€ na garantia, 338.141€ na isenção total de IMT proposta para 2027).

## E se é proprietário?

A procura jovem é o motor do mercado entre os 150 e os 350 mil euros na nossa zona. Com a garantia e o IMT Jovem a manterem-se, estes compradores continuam a chegar com financiamento a 100% e sem impostos de compra.

Se tem um imóvel neste segmento em Vila Franca de Xira, Alverca, Póvoa de Santa Iria ou Alhandra, o momento de o preparar para este comprador é agora — e não quando a concorrência já estiver toda à venda em janeiro.

---

**Quer saber quanto vale o seu imóvel para este comprador, ou quanto pode pedir ao banco?** Peça a sua Avaliação Gratuita ou fale comigo pelo WhatsApp — respondo pessoalmente.

*Informação baseada na proposta de Orçamento do Estado para 2027 e nas regras da garantia pública em vigor (outubro de 2026). Este artigo será actualizado quando a prorrogação for publicada.*
    `
  },
  {
    slug: 'imt-jovem-2027-isencao-orcamento-estado',
    titulo: 'IMT Jovem 2027: A Isenção Continua e o Limite Sobe Para 338.141€',
    resumo: 'A proposta de Orçamento do Estado para 2027 mantém a isenção de IMT e Imposto do Selo para quem tem até 35 anos e compra a primeira casa — e actualiza os limites. O que muda, quem tem direito e quanto pode poupar, com exemplos reais de Vila Franca de Xira.',
    data: '9 de Outubro de 2026',
    leitura: '5 min',
    categoria: 'Comprar',
    cor: '#d80c21',
    conteudo: `
## O que diz a proposta do OE2027

O Governo entregou a proposta de Orçamento do Estado para 2027 e o relatório é claro: as isenções de IMT e Imposto do Selo na compra da primeira habitação própria e permanente por jovens mantêm-se.

A novidade está nos valores. Os escalões do IMT são actualizados em 2,3%, o que empurra os limites da isenção para cima.

**Isenção total de IMT e Imposto do Selo:**

- 2026 (em vigor): até 330.539€
- **2027 (proposta): até 338.141€**

**Isenção parcial (8% só sobre o valor que excede o limite):**

- 2026 (em vigor): até 660.982€
- **2027 (proposta): até 676.185€**

Acima destes valores, não há qualquer benefício.

Em termos práticos: uma casa de 335.000€ comprada hoje por um jovem paga cerca de 393€ entre IMT e Imposto do Selo. Com a proposta para 2027, paga zero.

**Nota importante:** isto é uma proposta. O OE2027 é votado na generalidade a 28 de outubro e em votação final global a 24 de novembro. Os valores podem ainda ser ajustados no Parlamento.

## Quem tem direito

A isenção aplica-se se cumprir todas estas condições na data da escritura:

→ Ter 35 anos ou menos

→ Comprar para habitação própria e permanente (é ali que vai viver)

→ Não ser, nem ter sido nos três anos anteriores, proprietário de um imóvel habitacional

→ Não ser dependente de outra pessoa para efeitos de IRS no ano da compra

**Compra a dois?** Se só um dos compradores cumprir os requisitos, a isenção aplica-se apenas à quota-parte dessa pessoa. Este é o erro que mais vejo: casais em que um tem 36 anos e contam com a isenção total. Não têm. Faça as contas antes de assinar o CPCV.

## Quanto se poupa, na prática

Vamos ao concreto, com valores típicos de Vila Franca de Xira, Alverca e Póvoa de Santa Iria.

**Exemplo 1 — T2 de 250.000€ em Alverca**

- Comprador com 36 anos (regras gerais, escalões propostos para 2027): cerca de 6.800€ de IMT + 2.000€ de Imposto do Selo — **perto de 8.800€**
- Comprador com 32 anos (IMT Jovem): **0€**

São quase 9.000€ que ficam no bolso — o equivalente a uma cozinha nova ou a um ano de prestações de muitos créditos.

**Exemplo 2 — Moradia de 400.000€ na Póvoa de Santa Iria**

- Com IMT Jovem em 2027, paga 8% apenas sobre a parte acima de 338.141€ (61.859€): cerca de **4.950€ de IMT + 495€ de Imposto do Selo**
- Sem isenção, a factura seria várias vezes superior

*Valores indicativos, calculados com os escalões da proposta. O imposto incide sobre o maior valor entre o preço de compra e o VPT (valor patrimonial tributário).*

## Atenção: o que a isenção não cobre

A isenção não elimina todos os custos de compra. Continuam a existir:

- Emolumentos de escritura e registo (salvo enquadramento específico)
- Avaliação bancária e comissões de abertura do crédito
- Seguros de vida e multirriscos

Em regra, conte com 1.500€ a 3.000€ para estas despesas, dependendo do banco e do imóvel.

E para quem não é jovem: o limite de isenção de IMT na habitação própria e permanente também sobe, de 106.346€ para **108.792€**.

## Para quem está a pensar vender

Este artigo não é só para compradores.

Se tem um T2 ou T3 em Vila Franca de Xira, Alverca, Alhandra ou Póvoa abaixo dos 338 mil euros, o seu imóvel está exactamente no segmento onde a procura jovem vai continuar forte em 2027. Segundo o relatório do OE, estas medidas já beneficiaram quase 120 mil jovens, com um valor médio de compra de cerca de 200 mil euros.

Isto tem uma consequência directa no preço: compradores que não pagam IMT nem Imposto do Selo têm mais margem para pagar o valor pedido. Um imóvel bem posicionado neste intervalo vende mais depressa — e com menos negociação.

A pergunta certa não é "devo vender?". É: **"o meu imóvel está posicionado para este comprador?"** Preço, fotografias, documentação pronta para uma escritura rápida — tudo isso conta.

## Conclusão

O IMT Jovem mantém-se em 2027 e fica ligeiramente mais generoso. Para quem tem até 35 anos, comprar uma casa até 338.141€ continua a significar zero IMT e zero Imposto do Selo. Para quem vende neste segmento, significa um mercado com compradores motivados e financeiramente mais capazes.

---

**Está a comprar?** Faça a simulação antes de escolher o imóvel — use a página Viabilidade de Crédito. **Está a vender?** Peça a sua Avaliação Gratuita e saiba quanto vale o seu imóvel aos olhos deste comprador.

*Informação baseada na proposta de Orçamento do Estado para 2027 (outubro de 2026). Não dispensa a confirmação junto das Finanças ou de um profissional para o seu caso concreto.*
    `
  },
  {
    slug: 'precos-casas-portugal-2025-subida-recorde',
    titulo: 'Casas em Portugal Subiram 16,8% em 2025: O Que Isto Significa Para Si',
    resumo: 'O INE confirmou na semana passada: o preço mediano das casas em Portugal atingiu os 2.076€/m² em 2025, com uma subida de 16,8% face ao ano anterior. Perceba o que estes números significam para quem quer vender ou comprar — e porque é que estar bem informado vale dinheiro.',
    data: '28 de Abril de 2026',
    leitura: '5 min',
    categoria: 'Mercado',
    cor: '#051d40',
    conteudo: `
## Os números que ninguém pode ignorar

A 24 de abril de 2026, o Instituto Nacional de Estatística publicou os dados definitivos do mercado imobiliário português em 2025. Os números são históricos — e têm impacto directo no seu imóvel, seja para vender, comprar ou simplesmente saber quanto vale o que tem.

O preço mediano das casas em Portugal fixou-se nos **2.076 euros por metro quadrado** em 2025, um aumento de 16,8% face a 2024. No quarto trimestre, a subida chegou aos 17,5% — uma das maiores da União Europeia, segundo o Eurostat.

## O que aconteceu em cada região

Lisboa continua a ser o município mais caro do país, a aproximar-se dos **5.200 euros por m²**. A Grande Lisboa registou uma subida de 17% e a Área Metropolitana do Porto de 16%.

Mas o dado mais surpreendente vem de regiões que eram consideradas "baratas": o Baixo Alentejo subiu 27,6%. Santarém, Beja e a Guarda registaram subidas na ordem dos 22%. Isto já não é Lisboa e Porto — é um fenómeno nacional.

Para a zona de Vila Franca de Xira e arredores, os números acompanham a tendência da Grande Lisboa, com valorização consistente ao longo de 2025.

## O que estes dados significam se quer vender

Se tem um imóvel e está a pensar em vendê-lo, há uma boa e uma má notícia.

**A boa:** o mercado valoriza. O seu imóvel vale hoje mais do que valeu há 12 meses.

**A má:** muitos proprietários fixam preços com base em avaliações antigas ou em "achismos". Com um mercado em valorização tão rápida, uma avaliação feita há 6 meses já pode estar desactualizada.

A diferença entre ter uma avaliação rigorosa e actual e não ter pode significar dezenas de milhar de euros na escritura.

## E se quer comprar?

Os dados confirmam aquilo que quem procura casa já sente: os preços não vão descer de forma generalizada. O mercado entrou numa fase de maturidade — o ritmo de subida pode abrandar, mas a inversão não está no horizonte próximo.

Isto significa que adiar a decisão de compra raramente é uma boa estratégia. Cada mês de espera num mercado em valorização custa dinheiro.

## A minha leitura como especialista local

Estes números nacionais dizem muito, mas não dizem tudo. O mercado imobiliário funciona rua a rua, prédio a prédio. Um T3 em Alverca não compete com um T3 em Lisboa — mas também não fica imune às tendências da Grande Lisboa.

O que faço em cada avaliação é cruzar os dados nacionais e regionais com a realidade local: imóveis comparáveis vendidos recentemente na zona, tempo médio no mercado e perfil dos compradores activos. Só assim se chega a um preço que é simultaneamente justo para o vendedor e competitivo para o mercado.

---

**Tem curiosidade em saber quanto vale o seu imóvel neste mercado actual?** Faço avaliações gratuitas e sem compromisso. Entre em contacto comigo.
    `
  },
  {
    slug: 'imt-jovem-2026-guia-completo-primeira-casa',
    titulo: 'IMT Jovem 2026: Até 330.539€ Sem Pagar Impostos na Compra da Primeira Casa',
    resumo: 'Se tem até 35 anos e está a pensar comprar a sua primeira casa, pode poupar vários milhares de euros em impostos. O IMT Jovem foi actualizado em 2026 e a isenção total chegou aos 330.539€. Guia completo, sem complicações.',
    data: '25 de Abril de 2026',
    leitura: '6 min',
    categoria: 'Comprar',
    cor: '#d80c21',
    conteudo: `
## A medida que mais tem impacto nos jovens compradores

Desde que entrou em vigor em agosto de 2024, mais de 77.000 jovens já beneficiaram da isenção de IMT na compra da primeira casa. Em 2026, os limites foram actualizados em 2% e o programa mantém-se em vigor — com algumas regras que convém conhecer bem antes de ir à escritura.

## O que é o IMT Jovem e quanto poupa?

O IMT (Imposto Municipal sobre Transmissões) é um dos maiores custos na compra de casa. Num imóvel de 280.000€, por exemplo, pagaria normalmente cerca de 9.500€ só em IMT, mais 2.240€ de Imposto do Selo — um total de quase 12.000€ em impostos.

Com o IMT Jovem, se cumprir os requisitos, esse valor é **zero**.

## Os limites actualizados para 2026

Em 2026, os valores são os seguintes:

- **Isenção total**: imóveis até **330.539€** — não paga IMT nem Imposto do Selo na compra
- **Isenção parcial**: imóveis entre 330.539€ e 660.982€ — paga apenas sobre o valor que excede 330.539€
- **Sem isenção**: imóveis acima de 660.982€

## Quem pode beneficiar?

Para ter acesso à isenção tem de cumprir todas estas condições:

→ Ter 35 anos ou menos à data da escritura

→ Não ser considerado dependente no IRS no ano da compra

→ Não ser proprietário de nenhum imóvel habitacional, nem ter sido nos últimos 3 anos

→ O imóvel tem de ser para habitação própria e permanente

→ Ter domicílio fiscal em Portugal

## Atenção: herança ou doação pode excluir a isenção

Em março de 2026, surgiu muita confusão com esta questão. Se já recebeu ou recebe uma quota de um imóvel por herança ou doação — mesmo que pequena — pode perder o direito à isenção. Esta é uma das situações que convém confirmar com antecedência.

## A Garantia Pública do Estado: cumulável com o IMT Jovem

Além da isenção de impostos, os jovens até 35 anos têm acesso à Garantia Pública do Estado, que permite financiamento até 100% do valor do imóvel — sem precisar dos habituais 10% a 20% de entrada. Esta garantia é válida até 31 de dezembro de 2026 para imóveis até 450.000€.

Ou seja, um jovem comprador pode, em simultâneo, não pagar entrada (Garantia Pública) e não pagar impostos de aquisição (IMT Jovem). É a combinação mais poderosa que existe actualmente para comprar a primeira casa em Portugal.

## Como é aplicada na prática

A isenção é processada na declaração Modelo 1 de IMT, normalmente tratada pelo solicitador ou pelo banco que acompanha o crédito. Na escritura, a isenção já aparece reflectida. Não tem de pedir — acontece automaticamente se os dados estiverem correctos.

## O meu papel neste processo

Com uma equipa de intermediários de crédito a trabalhar comigo e eu, como consultor RE/MAX, acompanho clientes jovens em todo este processo — da avaliação do imóvel, à preparação da documentação, à ligação com o banco. Garantir que aproveitam todos os apoios disponíveis faz parte do serviço.

---

**Tem até 35 anos e está a pensar comprar casa em Vila Franca de Xira ou arredores?** Fale comigo. Ajudo-o a perceber o que se aplica ao seu caso concreto.
    `
  },
  {
    slug: 'vender-casa-2026-momento-certo-estrategia',
    titulo: 'Vender Casa em 2026: Está no Momento Certo — Mas a Estratégia É Tudo',
    resumo: 'Os preços estão em máximos históricos, há compradores no mercado e as condições de financiamento melhoraram. Mas vender bem não é automático. Saiba o que está a acontecer no mercado agora e o que diferencia uma venda rápida ao preço certo de um imóvel parado meses.',
    data: '29 de Abril de 2026',
    leitura: '7 min',
    categoria: 'Vender',
    cor: '#0a3166',
    conteudo: `
## O mercado está do seu lado — mas isso não chega

Os dados de 2025 são claros: os preços subiram 16,8% a nível nacional, o mercado transaccionou 164.677 imóveis, e a Grande Lisboa registou valorizações de 17%. Para quem quer vender, este é provavelmente o melhor momento da última década.

Mas há um paradoxo que vejo no terreno regularmente: mesmo com o mercado favorável, há imóveis que ficam meses sem vender. E há imóveis que recebem propostas múltiplas na primeira semana. A diferença não está no mercado — está na estratégia.

## O que está a acontecer no mercado agora (Abril 2026)

Dados recentes do sector confirmam uma tendência que já sentia: o ritmo de subida dos preços começa a abrandar ligeiramente, sem que isso signifique queda. O mercado está a entrar numa fase de maturidade.

O que isto significa na prática:

→ Os compradores estão mais informados e mais cautelosos — já não compram "à primeira vista"

→ Há mais margem para negociação do que havia em 2023 e 2024

→ Um imóvel mal posicionado em termos de preço fica mais tempo no mercado

→ Um imóvel bem preparado e bem promovido ainda vende rápido e bem

Nunca foi tão importante fazer as coisas bem.

## Os erros mais comuns que fazem um imóvel ficar parado

Depois de acompanhar dezenas de vendas, identifico os mesmos padrões nos imóveis que demoram:

**Preço emocional vs. preço de mercado**
O proprietário sabe o que pagou, o que investiu, o que precisa. O comprador só sabe o que está a ver. Um estudo comparativo rigoroso é o único antídoto para esta tensão.

**Fotografias amadoras**
Mais de 90% das pesquisas começam online. Uma fotografia escura, com ângulo errado, com roupa no sofá, afasta compradores antes de qualquer visita. É irrecuperável.

**Ausência de estratégia de promoção**
Colocar num portal e esperar não é estratégia. É esperança. Com a concorrência que existe, o imóvel tem de estar onde os compradores estão — portais, redes sociais, base de dados de compradores activos, rede RE/MAX.

**Falta de preparação para as visitas**
A primeira impressão é decisiva. Pequenas intervenções — uma mão de tinta, uma limpeza profunda, reorganizar os espaços — podem fazer diferença na percepção de valor.

## O que a nova construção está a fazer ao mercado

A nova construção ganhou peso relativo em 2026, passando de 4,6% para 5,0% da oferta total. Com um preço médio de 647.500€ nos imóveis novos face a 425.000€ nos usados, há uma segmentação clara.

Para quem vende usado, a vantagem competitiva está na localização, na personalidade do imóvel e no preço justo. Imóveis usados bem apresentados continuam a ter procura forte — especialmente em zonas com boa acessibilidade e serviços, como é o caso de Vila Franca de Xira e Alverca.

## A minha abordagem para cada imóvel

Não há uma receita única. O que faço é construir um plano à medida de cada imóvel e cada proprietário:

→ Estudo comparativo de mercado actualizado, com dados reais de vendas recentes

→ Definição do preço estratégico — nem alto demais (afasta compradores), nem baixo demais (perde valor)

→ Fotografia profissional e, consoante o imóvel, drone e virtual tour

→ Promoção omnicanal: portais nacionais, redes sociais, base RE/MAX, distribuição local

→ Gestão activa das visitas e das propostas — incluindo criação de urgência quando há múltiplos interessados

→ Acompanhamento jurídico e burocrático até à escritura

---

**Tem um imóvel para vender em Vila Franca de Xira ou arredores?** Peço-lhe uma avaliação gratuita e mostro-lhe o que o mercado está realmente a pagar na sua zona. Sem compromisso.
    `
  },
  {
    slug: 'como-vender-casa-rapido-vila-franca-xira',
    titulo: 'Como Vender a Sua Casa Mais Rápido em Vila Franca de Xira',
    resumo: 'Vender um imóvel pode parecer complicado, mas com a estratégia certa é possível fechar negócio em poucas semanas. Descubra os 7 passos que uso com os meus clientes para vender mais rápido e ao melhor preço.',
    data: '18 de Março de 2026',
    leitura: '5 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## Vender rápido não é sorte — é estratégia

No mercado imobiliário de Vila Franca de Xira, os imóveis bem preparados e bem apresentados vendem-se em média 3 a 4 vezes mais rápido do que os restantes. Aqui estão os 7 passos que faço com cada cliente:

## 1. Avaliação realista do mercado

O erro mais comum é fixar um preço emocional — quanto o proprietário acha que vale, não quanto o mercado está disposto a pagar. Uma avaliação comparativa com imóveis recentemente vendidos na mesma zona é o ponto de partida obrigatório.

## 2. Pequenas obras, grande impacto

Não é preciso renovar a casa toda. Pintura fresca, uma torneira nova, limpar a fachada — pequenas intervenções que custam pouco podem aumentar o valor percebido em 5% a 10%.

## 3. Home staging

Organizar e despersonalizar o espaço permite que os compradores se imaginem a viver ali. Tirar fotos de família, guardar objectos pessoais e criar ambientes neutros faz uma diferença enorme nas visitas.

## 4. Fotografia profissional

Mais de 90% das buscas de imóveis começam online. Fotos com boa luz, ângulos certos e qualidade profissional são a diferença entre 10 contactos e 100 contactos.

## 5. Presença online estratégica

O imóvel tem de estar visível onde os compradores procuram: portais imobiliários, redes sociais e o site do consultor. Com o RE/MAX, os imóveis têm distribuição nacional e internacional.

## 6. Qualificação dos compradores

Não adianta ter 50 visitas de pessoas que não têm capacidade financeira. Qualificar os interessados antes das visitas poupa tempo a toda a gente.

## 7. Negociação profissional

A fase da negociação é onde muitos negócios se perdem por falta de experiência. Um consultor experiente sabe quando ceder, quando manter e como fechar ao melhor preço para o vendedor.

---

**Quer saber quanto vale a sua casa em Vila Franca de Xira?** Peço-lhe uma avaliação gratuita e sem compromisso. Entre em contacto comigo diretamente.
    `
  },
  {
    slug: 'comprar-casa-portugal-2026-guia-completo',
    titulo: 'Comprar Casa em Portugal em 2026: O Guia Completo',
    resumo: 'Desde a escolha do imóvel até à escritura, comprar casa envolve muitas decisões. Este guia explica cada etapa do processo, os custos reais envolvidos e os erros mais comuns a evitar.',
    data: '15 de Março de 2026',
    leitura: '8 min',
    categoria: 'Comprar',
    cor: '#051d40',
    conteudo: `
## Comprar casa é uma das maiores decisões da vida

Em Portugal, o processo de compra tem várias etapas e custos que nem sempre são óbvios. Este guia dá-lhe uma visão clara de tudo o que precisa de saber.

## Etapa 1: Definir o orçamento real

Antes de visitar qualquer imóvel, calcule o seu orçamento total — não apenas o preço de compra, mas também os custos adicionais:

- **IMT** (Imposto Municipal sobre Transmissões): varia entre 0% e 7,5% consoante o valor e finalidade
- **Imposto do Selo**: 0,8% do valor de compra
- **Escritura e registo**: entre 800€ e 1.500€
- **Comissão bancária** (se usar crédito habitação): cerca de 1% do valor do empréstimo

## Etapa 2: Financiamento

Se precisar de crédito habitação, contacte vários bancos antes de fazer uma oferta. A pré-aprovação dá-lhe poder negocial e evita surpresas.

Nota importante: os bancos financiam normalmente até 80% do valor de avaliação (90% para jovens até 35 anos em 2026).

## Etapa 3: Procura e visitas

Não visite apenas 2 ou 3 imóveis. Visite pelo menos 10 para ter uma noção real do mercado local. Compare preço por m², estado de conservação, custos de condomínio, certificado energético, localização e acessos.

## Etapa 4: Proposta e CPCV

Quando encontrar o imóvel certo, faz-se uma proposta formal. Se aceite, assina-se o Contrato Promessa de Compra e Venda com pagamento de um sinal (normalmente 10% a 20%).

Atenção: se desistir sem justa causa, perde o sinal. Se o vendedor desistir, devolve o dobro.

## Etapa 5: Due diligence

Antes da escritura, verifique sempre: certidão do registo predial, caderneta predial actualizada, licença de habitação e certificado energético válido.

## Etapa 6: Escritura

A escritura é o acto que transfere a propriedade. É feita num notário ou conservatória e requer a presença de todas as partes.

---

**Está a pensar comprar casa em Vila Franca de Xira ou arredores?** Acompanho todo o processo gratuitamente, desde a procura até à escritura. Entre em contacto comigo.
    `
  },
  {
    slug: 'investir-imoveis-vila-franca-xira-2026',
    titulo: 'Investir em Imóveis em Vila Franca de Xira: Vale a Pena em 2026?',
    resumo: 'Vila Franca de Xira tem crescido como destino de investimento imobiliário. Preços ainda acessíveis, boa ligação a Lisboa e crescimento populacional fazem desta zona uma oportunidade real. Veja os números.',
    data: '10 de Março de 2026',
    leitura: '6 min',
    categoria: 'Investir',
    cor: '#0a3166',
    conteudo: `
## Por que investir em Vila Franca de Xira?

Com os preços em Lisboa a tornarem-se inacessíveis para muitos compradores, os concelhos limítrofes têm registado um crescimento significativo. Vila Franca de Xira é um desses casos.

## Os números do mercado local

O preço médio por m² em Vila Franca de Xira ronda os 1.800€ a 2.500€, dependendo da localização e tipologia — significativamente abaixo de Lisboa e abaixo da média da Área Metropolitana de Lisboa.

Para quem compra para arrendamento, as rendas médias de um T2 em Alverca ou Póvoa de Santa Iria variam entre 800€ e 1.100€/mês, o que representa uma rentabilidade bruta de 4% a 6% — acima da maioria dos depósitos a prazo.

## Factores que valorizam o concelho

- **Linha de comboio de Azambuja**: ligação directa ao Oriente e Santa Apolónia em 30 a 45 minutos
- **Acesso A1 e A10**: mobilidade rápida para Lisboa, Loures e Setúbal
- **Hospital de Vila Franca de Xira**: equipamento de referência que atrai população
- **Preços ainda competitivos**: há margem de valorização face a concelhos vizinhos já mais valorizados

## Riscos a considerar

Como qualquer investimento, há riscos: mercado de arrendamento mais pequeno que Lisboa, liquidez menor na revenda e necessidade de gestão activa do imóvel.

## A minha opinião como especialista local

Para investimento a médio-longo prazo (5 a 10 anos), Vila Franca de Xira apresenta um perfil de risco-retorno interessante. A combinação de preços ainda acessíveis, boa acessibilidade e crescimento demográfico cria condições favoráveis.

---

**Quer analisar uma oportunidade de investimento específica?** Posso fazer uma análise de rentabilidade personalizada. Entre em contacto comigo — sem custos e sem compromisso.
    `
  },
  {
    slug: 'quanto-vale-casa-loures-guia-2026',
    titulo: 'Quanto Vale a Minha Casa em Loures? Guia 2026',
    resumo: 'Os preços em Loures dispararam nos últimos anos. Saiba exatamente como calcular o valor real do seu imóvel, que fatores pesam mais e porque uma avaliação online nunca substitui uma análise feita por um especialista local.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## Loures está mais valioso do que pensa

Se vive em Loures e há uns anos não pensa no valor da sua casa, prepare-se para uma surpresa. O concelho tem registado uma das valorizações mais consistentes da Área Metropolitana de Lisboa, impulsionado pela proximidade a Lisboa, pela linha ferroviária e pelos acessos pela A8 e A1.

Em 2026, os apartamentos em Loures atingem em média **4.400€/m²** e as moradias **3.438€/m²** — valores que, em muitas freguesias, já superam os 4.500€/m² para imóveis bem localizados e em bom estado.

## Os 5 fatores que determinam o valor da sua casa

**Localização dentro do concelho**
Sacavém, Prior Velho, Moscavide e Portela — mais próximos de Lisboa e do Aeroporto — têm valores acima da média do concelho. Zonas mais afastadas têm preços mais moderados, mas continuam a valorizar.

**Estado de conservação**
Um imóvel renovado pode valer 15% a 25% mais do que um imóvel idêntico por renovar. Cozinha e casas de banho são as áreas que mais pesam na perceção de valor.

**Eficiência energética**
Um certificado energético A ou B já é um argumento de venda real, com impacto direto no valor e na rapidez de venda — sobretudo junto de compradores mais jovens.

**Exposição solar e vistas**
Apartamentos com boa exposição e sem prédios em frente conseguem destacar-se claramente da concorrência direta no mesmo prédio ou rua.

**Garagem e arrumos**
Em Loures, um lugar de garagem fechado pode valer entre 15.000€ e 25.000€ adicionais, dependendo da freguesia — e a sua ausência é um dos motivos mais comuns para uma venda demorar mais tempo.

## Por que as avaliações automáticas online falham

As ferramentas de avaliação automática (AVM) baseiam-se em médias estatísticas por zona — não veem o estado real do imóvel, não sabem se há obras a fazer, não conhecem a procura específica daquela rua naquele momento. É frequente verem-se diferenças de 10% a 20% entre o valor automático e o valor real de mercado.

## Como faço a avaliação do seu imóvel

Quando avalio uma casa em Loures, cruzo três fontes de informação: transações reais e recentes na mesma freguesia (não médias do concelho todo), o estado específico do seu imóvel visto presencialmente, e a procura ativa que tenho de compradores à procura de imóveis semelhantes nessa zona.

Este cruzamento é o que permite chegar a um preço que não é nem optimista demais (o que faz o imóvel ficar parado meses) nem conservador demais (o que faz perder dinheiro na venda).

---

**Quer saber exatamente quanto vale a sua casa em Loures?** Faço avaliações gratuitas e sem compromisso, com visita presencial e relatório detalhado. Entre em contacto comigo.
    `
  },
  {
    slug: '5-erros-vender-casa-vila-franca-xira',
    titulo: '5 Erros Que Custam Dinheiro a Quem Vende Casa em Vila Franca de Xira',
    resumo: 'Vender uma casa não é só colocar um anúncio. Conheço de perto os erros que fazem proprietários perder dinheiro ou meses de tempo em Vila Franca de Xira — e como evitá-los desde o primeiro dia.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## Vender mal custa mais do que pensa

Em mais de uma década no terreno em Vila Franca de Xira, vejo os mesmos 5 erros repetirem-se constantemente — e todos eles têm um custo real, mensurável, em euros ou em meses perdidos.

## Erro 1: Fixar o preço com base no que "preciso" em vez do que vale

É talvez o erro mais comum e mais caro. O proprietário define o preço com base no que pagou, no que investiu em obras, ou no que precisa para a próxima compra — não no que o mercado está disposto a pagar agora.

**Custo real**: um imóvel sobrevalorizado em 10% pode ficar 3 a 4 vezes mais tempo no mercado, e acaba muitas vezes por vender por menos do que valeria se tivesse entrado ao preço certo desde o início.

## Erro 2: Não preparar o imóvel antes das fotografias

Fotografar a casa "como está" — com roupa estendida, objetos pessoais à vista, iluminação desligada — é um erro que afasta compradores antes de qualquer visita acontecer.

**Custo real**: imóveis com fotografia profissional e casa preparada recebem em média 3 vezes mais contactos do que os que não investem nesta fase.

## Erro 3: Aceitar a primeira proposta sem comparar

Em mercados dinâmicos como Alverca ou Póvoa de Santa Iria, a primeira proposta nem sempre é a melhor. Aceitar de imediato, sem dar tempo a que o mercado responda, pode significar deixar dinheiro na mesa.

**Custo real**: uma negociação mal conduzida pode custar entre 5.000€ e 15.000€ num imóvel médio na zona.

## Erro 4: Ignorar o direito de preferência se houver inquilino

Se o imóvel está arrendado, o senhorio é obrigado por lei a notificar o inquilino da intenção de venda, dando-lhe 30 dias para exercer o direito de preferência (caso cumpra os requisitos legais). Ignorar este passo pode anular a venda já feita a terceiros.

**Custo real**: processos judiciais, atrasos de meses, e em casos extremos, perda do comprador já encontrado.

## Erro 5: Não ter a documentação organizada antes de anunciar

Caderneta predial desatualizada, certificado energético expirado, ou ausência de licença de habitação são problemas que só aparecem quando já há um comprador interessado — e que podem atrasar ou mesmo travar a escritura.

**Custo real**: perda de compradores impacientes e atraso de semanas ou meses no processo.

## Como evito estes erros com os meus clientes

Antes de qualquer imóvel ir para o mercado, faço sempre: estudo comparativo de mercado com dados reais e recentes da zona, checklist de documentação completa, sessão de preparação do imóvel (home staging) e fotografia profissional. É este trabalho de base que evita 90% dos problemas que aparecem depois.

---

**Está a pensar vender em Vila Franca de Xira ou arredores?** Faço uma avaliação gratuita e ajudo-o a evitar estes 5 erros desde o primeiro dia. Entre em contacto comigo.
    `
  },
  {
    slug: 'comprar-casa-mafra-mercado-2026',
    titulo: 'Comprar Casa em Mafra: O Que Esperar do Mercado em 2026',
    resumo: 'Mafra tornou-se um dos concelhos mais procurados na zona norte de Lisboa. Veja os preços reais de 2026, as freguesias com maior procura e o que considerar antes de comprar.',
    data: '3 de Junho de 2026',
    leitura: '7 min',
    categoria: 'Comprar',
    cor: '#051d40',
    conteudo: `
## Mafra: um dos concelhos que mais valorizou

Se está a pensar comprar casa em Mafra, é importante saber que o concelho deixou de ser uma "alternativa barata" a Lisboa — tornou-se um destino procurado por mérito próprio. A proximidade com a Ericeira, reserva mundial de surf, e os acessos pela A21 e A8 transformaram a procura na região.

Em 2026, os apartamentos em Mafra atingem em média **4.661€/m²** e as moradias **3.790€/m²** — valores que já competem com concelhos tradicionalmente mais valorizados da Grande Lisboa.

## As zonas com maior procura em Mafra

**Ericeira e zona costeira**
A procura aqui é impulsionada por compradores nacionais e estrangeiros que valorizam o estilo de vida costeiro associado ao surf. Os preços nesta zona já se aproximam ou superam a média do concelho.

**Malveira**
Bem servida em comércio e serviços, com boa ligação a Lisboa, atrai sobretudo famílias jovens que procuram espaço sem perder acessibilidade.

**Mafra (vila) e Enxara do Bispo**
Zonas mais tradicionais, com moradias e terrenos maiores, procuradas por quem valoriza espaço exterior e tranquilidade.

## Quanto custa realmente comprar em Mafra em 2026

Além do preço do imóvel, é essencial contar com os custos de aquisição:

- **IMT**: entre 0% (até 106.346€ para HPP) e 7,5%, dependendo do valor e finalidade
- **Imposto do Selo**: 0,8% do valor de aquisição
- **Escritura e registo**: entre 800€ e 1.500€
- **IMT Jovem**: se tiver até 35 anos, pode estar isento até 330.539€

Para uma moradia típica em Mafra de 350.000€, sem isenções aplicáveis, os impostos de aquisição rondam os 14.000€ a 16.000€.

## O que esperar do mercado em 2026

Os dados nacionais do INE confirmam uma subida de 16,8% nos preços em 2025, com tendência a estabilizar (sem inverter) em 2026. Para Mafra especificamente, a procura mantém-se forte, sobretudo por tipologias T3 e T4 com espaço exterior — o que significa que imóveis com jardim ou terreno tendem a vender rapidamente quando bem precificados.

## O que considerar antes de comprar em Mafra

→ **Acessos**: confirme o tempo real de deslocação para Lisboa no horário que vai usar — a A21 pode ter trânsito em horas de ponta

→ **Infraestruturas**: nem todas as zonas têm a mesma cobertura de saneamento e rede elétrica, especialmente em zonas mais rurais

→ **Certificado energético**: moradias mais antigas podem ter custos energéticos elevados — peça sempre o certificado antes de decidir

→ **PDM e licenciamento**: se pensa em obras ou ampliações, confirme o Plano Diretor Municipal antes de comprar

## Como vos posso ajudar

Acompanho clientes em todo o processo de compra em Mafra — desde a procura inicial, à avaliação do imóvel, à negociação e até à escritura, incluindo a ligação com bancos e intermediários de crédito para garantir as melhores condições.

---

**Está a pensar comprar casa em Mafra?** Posso ajudá-lo a encontrar o imóvel certo e a navegar todo o processo. Entre em contacto comigo — sem custos.
    `
  },
  {
    slug: 'vender-casa-alenquer-prazos-precos-estrategia',
    titulo: 'Vender Casa em Alenquer: Prazos, Preços e Estratégia',
    resumo: 'Alenquer está numa fase de forte valorização, mas ainda com preços competitivos. Saiba quanto tempo demora a vender, que preços esperar e qual a melhor estratégia para o seu imóvel.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## Alenquer: valorização a acontecer agora

Alenquer é, hoje, um dos concelhos com maior potencial de valorização na zona norte de Lisboa — e isso é uma boa notícia para quem quer vender. Com paisagens que vão do vale do Tejo às encostas vinhateiras, o concelho atrai cada vez mais famílias e investidores que procuram espaço e tranquilidade a preços ainda competitivos face a concelhos vizinhos já mais valorizados.

Em 2026, o preço mediano dos imóveis em Alenquer situa-se nos **199.000€**, com valores por metro quadrado entre **1.600€ e 2.400€**, dependendo da localização e tipo de imóvel.

## Quanto tempo demora a vender em Alenquer

O tempo médio de venda em Alenquer situa-se entre **60 e 100 dias**, mais longo do que em concelhos com mercado mais aquecido como Loures ou Vila Franca de Xira. Isto não é necessariamente uma desvantagem — significa que há mais espaço para uma estratégia bem preparada, mas também que o erro de sobrevalorizar o imóvel se paga com meses adicionais no mercado.

## Os fatores que mais influenciam o preço em Alenquer

**Tipo de imóvel**
Moradias com terreno são as mais procuradas no concelho — espaço exterior é um dos principais argumentos de venda em Alenquer, ao contrário de zonas mais urbanas onde o foco é a localização.

**Proximidade à vila de Alenquer**
A vila concentra mais comércio e serviços, o que se reflete em preços ligeiramente acima da média de freguesias mais rurais como Abrigada, Aldeia Galega da Merceana ou Cabanas de Torres.

**Acessos**
A proximidade à A1 e à linha ferroviária do Norte (que liga a Lisboa em menos de 45 minutos) é um argumento de peso para compradores que trabalham na capital mas procuram mais espaço pelo mesmo orçamento.

## Estratégia recomendada para vender em Alenquer

Dado o tempo médio de venda mais longo, a estratégia certa em Alenquer assenta em três pilares:

**1. Preço de entrada correto desde o primeiro dia**
Num mercado com ciclo de venda mais longo, um preço errado tem mais tempo para "queimar" o imóvel — compradores que veem o mesmo anúncio meses a fio tendem a desconfiar e a negociar mais agressivamente.

**2. Destaque do espaço exterior e potencial da propriedade**
Em Alenquer, terreno, vista e potencial de ampliação pesam mais na decisão do que em mercados urbanos. A apresentação deve dar protagonismo a estes elementos.

**3. Promoção alargada à área metropolitana**
Muitos compradores em Alenquer vêm de Lisboa ou de concelhos vizinhos mais caros — a promoção do imóvel não se deve limitar ao concelho, mas alcançar quem procura "mais espaço pelo mesmo dinheiro" na área metropolitana.

## O meu acompanhamento

Faço uma avaliação rigorosa baseada em transações reais e recentes na zona específica do seu imóvel, e desenho uma estratégia de promoção que vai além dos portais imobiliários — incluindo a rede RE/MAX e a minha base de compradores ativos.

---

**Tem um imóvel para vender em Alenquer?** Faço uma avaliação gratuita e desenho consigo a estratégia certa para o seu caso. Entre em contacto comigo.
    `
  },
  {
    slug: 'benavente-vale-a-pena-analise-mercado',
    titulo: 'Benavente Vale a Pena? Análise de Mercado Imobiliário',
    resumo: 'Benavente está a tornar-se uma das apostas mais inteligentes na margem sul do Tejo. Veja os números reais, as vantagens do concelho e se realmente vale a pena comprar ou investir aqui em 2026.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Investir',
    cor: '#0a3166',
    conteudo: `
## Benavente: a pergunta que todos fazem

"Vale a pena comprar em Benavente?" É a pergunta que mais ouço de clientes que comparam este concelho com Vila Franca de Xira ou com a margem norte do Tejo. A resposta, com base nos números reais de 2026, é claramente sim — para o perfil certo de comprador.

## Os números que importam

Benavente continua a ser um dos concelhos com **melhor relação qualidade-preço** na Área Metropolitana de Lisboa, com valores entre **1.400€/m² e 2.100€/m²** — significativamente abaixo da margem norte do Tejo e de concelhos como Loures ou Vila Franca de Xira.

O tempo médio de venda situa-se entre **60 e 120 dias**, mais longo do que em mercados mais aquecidos, o que reflete uma procura ainda em consolidação — mas também significa que quem compra agora pode beneficiar de uma valorização futura semelhante à que já aconteceu noutros concelhos da região.

## Porque é que Benavente está a ganhar atenção

**Localização estratégica**
Situado na margem sul do Tejo, entre Lisboa e o Alentejo, com acesso pela A10 e pela ponte Marechal Carmona, o centro de Lisboa fica a menos de 40 minutos.

**Espaço e tranquilidade**
As moradias com terreno são a tipologia mais procurada — Benavente oferece o que Lisboa e os concelhos mais valorizados já não conseguem: espaço a preços acessíveis.

**Natureza e qualidade de vida**
A existência de zonas naturais protegidas e o acesso direto ao rio Tejo são argumentos que pesam cada vez mais para quem procura qualidade de vida e não só metros quadrados.

**Localidades em crescimento**
Samora Correia, Benavente (vila), Santo Estêvão e Barrosa têm registado procura crescente, cada uma com um perfil distinto — Samora Correia mais urbana, Barrosa e Santo Estêvão mais rurais.

## Para quem é que Benavente faz sentido?

Benavente não é o concelho certo para quem precisa de estar em Lisboa todos os dias com deslocações rápidas e sem trânsito. Mas é uma excelente opção para:

→ Famílias que trabalham em regime híbrido e valorizam espaço sobre proximidade absoluta

→ Investidores que procuram entrada num mercado ainda com margem de valorização

→ Quem procura moradia com terreno a um preço impossível de encontrar mais perto de Lisboa

→ Reformados ou pré-reformados que procuram tranquilidade e qualidade de vida

## Os riscos a ter em conta

Sendo um mercado com ciclo de venda mais longo, comprar em Benavente exige paciência — tanto na compra (pode haver menos oferta de qualidade disponível num dado momento) como na eventual revenda futura. A liquidez é menor do que em mercados mais dinâmicos.

## A minha avaliação como consultor

Benavente vale a pena para quem entende o concelho como um investimento de médio-longo prazo, não como uma compra especulativa de curto prazo. A combinação de preços ainda competitivos, qualidade de vida e crescimento sustentado da procura cria condições reais de valorização nos próximos anos — mas sem garantias de retorno rápido.

---

**Está a considerar comprar ou investir em Benavente?** Faço uma análise personalizada à sua situação e objetivos. Entre em contacto comigo — sem custos.
    `
  },
  {
    slug: 'imt-imposto-selo-mais-valias-2026-o-que-vais-pagar',
    titulo: 'IMT, IS e Mais-Valias: O Que Vais Pagar Quando Vendes',
    resumo: 'Vender uma casa em 2026 envolve impostos que muitos proprietários só descobrem tarde demais. Explico de forma simples o que vai pagar de Imposto do Selo e mais-valias — e como pode reduzir ou eliminar legalmente esse imposto.',
    data: '3 de Junho de 2026',
    leitura: '8 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## Quem vende também paga impostos

Há uma confusão comum: muita gente associa o IMT apenas a quem compra. É verdade — o IMT é pago pelo comprador, não pelo vendedor. Mas quem vende não fica isento de impostos: entra em jogo o **imposto sobre mais-valias**, que pode ter um impacto real no que efetivamente recebe pela venda.

## O que são mais-valias imobiliárias

A mais-valia é o ganho obtido com a venda de um imóvel — a diferença entre o valor de venda e o valor pelo qual o imóvel foi adquirido, com alguns ajustes. Este ganho está sujeito a tributação em sede de IRS, regulado pelo artigo 10.º do Código do IRS.

A fórmula base é:

**Mais-Valia = Valor de Realização − (Valor de Aquisição × Coeficiente de Desvalorização) − Encargos com a Valorização − Despesas com a Venda**

O coeficiente de desvalorização monetária atualiza o valor de aquisição em função da inflação acumulada desde a data de compra — o que normalmente reduz a mais-valia tributável, especialmente em imóveis comprados há muitos anos.

## A regra mais importante: só 50% é tributado

Para residentes em Portugal, apenas **50% do ganho** conta para efeitos de imposto. Esse valor soma-se aos restantes rendimentos do ano (salário, pensões, rendas) e é tributado pela taxa do escalão de IRS correspondente — não há uma taxa fixa única.

## Que despesas pode deduzir

Antes de calcular a mais-valia, pode deduzir:

- **Despesas de aquisição**: IMT, Imposto do Selo, notário, escritura e registos pagos na altura da compra
- **Obras de valorização**: realizadas nos últimos 12 anos, desde que tenha faturas em seu nome
- **Encargos com a venda**: comissão da agência imobiliária e certificado energético

Guardar todas estas faturas ao longo dos anos é, por isso, uma das recomendações mais valiosas que dou aos meus clientes — muitas vezes representa milhares de euros de diferença no imposto final.

## A isenção mais importante: habitação própria e permanente

Se o imóvel vendido era a sua habitação própria e permanente (HPP), pode ficar **totalmente isento** de imposto sobre mais-valias se reinvestir o produto da venda numa nova habitação própria e permanente no prazo de 36 meses.

Há também a possibilidade de isenção se o reinvestimento for num plano de poupança-reforma (PPR), aplicável a partir dos 65 anos ou em situação de reforma, em determinadas condições.

## Casos especiais que geram confusão

**Imóveis herdados ou doados**
Quando vende um imóvel recebido por herança ou doação, o valor de aquisição não é o que o falecido pagou — é o **Valor Patrimonial Tributário (VPT)** à data da transmissão, o mesmo que serviu de base ao Imposto do Selo na altura da herança. A data de aquisição é a data do óbito, e a partilha posterior não altera essa data.

**Venda de quota-parte herdada**
Se vende apenas a sua quota-parte de um imóvel herdado, o valor de aquisição considerado é proporcional à percentagem que lhe pertence.

**Não residentes em Portugal**
Quem reside fora de Portugal e vende um imóvel aqui já não está, em regra, sujeito à antiga taxa fixa de 28% sobre a totalidade do ganho — o regime aproximou-se do dos residentes.

## Como e quando se declara

A venda declara-se no **Anexo G** da declaração de IRS. Quem vende em 2025, declara em 2026, entre 1 de abril e 30 de junho. Quem vender em 2026, declarará entre abril e junho de 2027.

## O erro mais caro que vejo repetidamente

Usar um valor de aquisição errado — sobretudo em casos de herança — é o erro que mais inflaciona a mais-valia e leva a pagar imposto a mais sem necessidade. Confirmar este valor com um contabilista antes de vender pode poupar-lhe centenas ou milhares de euros.

## O meu papel neste processo

Embora a parte fiscal seja sempre tratada por um contabilista ou pela própria Autoridade Tributária, faço questão de alertar todos os meus clientes vendedores para este tema desde o início do processo — para que possam preparar documentação, recolher faturas de obras e planear eventual reinvestimento com tempo, em vez de descobrir tudo isto já depois da escritura.

---

**Vai vender o seu imóvel e tem dúvidas sobre os impostos envolvidos?** Posso ajudá-lo a entender o processo e, se necessário, recomendar um contabilista de confiança. Entre em contacto comigo.
    `
  },
  {
    slug: 'preparar-casa-para-vender-sem-gastar-1000-euros',
    titulo: 'Como Preparar a Tua Casa Para Vender (Sem Gastar 1000€)',
    resumo: 'Não precisa de uma renovação completa para vender melhor e mais rápido. Estas são as intervenções de baixo custo que fazem a maior diferença na perceção de valor de um imóvel.',
    data: '3 de Junho de 2026',
    leitura: '5 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## O mito da "grande renovação" antes de vender

Um dos enganos mais comuns entre proprietários é pensar que precisam de gastar 5.000€, 10.000€ ou mais em obras antes de pôr a casa à venda. Na maioria dos casos, isso é dinheiro mal investido — o retorno raramente compensa. O que faz mesmo diferença são intervenções pequenas, baratas, mas estrategicamente certas.

## 1. Limpeza profunda (custo: 50€ a 150€)

Parece óbvio, mas é o passo mais subestimado. Uma limpeza profissional profunda — vidros, rodapés, eletrodomésticos, casas de banho — muda completamente a primeira impressão de um imóvel. É, de longe, o investimento com melhor relação custo-benefício.

## 2. Despersonalizar e organizar (custo: 0€)

Retirar fotografias de família, objetos religiosos ou políticos, e excesso de decoração pessoal permite que o comprador se imagine a viver naquele espaço. Arrumar armários e reduzir o volume de objetos visíveis também aumenta a perceção de espaço — sem gastar nada.

## 3. Pintura de paredes neutras (custo: 100€ a 300€ por divisão)

Não é preciso pintar a casa toda. Foque-se nas paredes com cores fortes, manchas ou marcas visíveis. Tons neutros (branco, cinza claro, beige) agradam a mais compradores e fazem os espaços parecerem maiores e mais luminosos.

## 4. Iluminação (custo: 30€ a 100€)

Substituir lâmpadas fundidas ou amareladas por lâmpadas de luz branca/neutra em todos os espaços faz uma diferença imediata nas fotografias e nas visitas presenciais. Espaços bem iluminados parecem maiores e mais cuidados.

## 5. Pequenas reparações visíveis (custo: 50€ a 200€)

Uma torneira a pingar, uma porta que range, um puxador solto — são detalhes pequenos que, somados, comunicam "esta casa não foi cuidada". Resolver estes pormenores antes das visitas evita que o comprador comece a questionar o que mais pode estar mal.

## 6. Exterior e entrada (custo: 0€ a 100€)

A primeira impressão começa antes de entrar na casa. Limpar a entrada, cortar relva ou plantas, e garantir que a campainha e a fechadura funcionam bem custa pouco e tem impacto desproporcional na primeira impressão.

## 7. Aromatização e ambiente (custo: 0€ a 30€)

Ventilar bem a casa antes das visitas, eliminar odores (especialmente de animais ou tabaco), e criar um ambiente agradável (cortinas abertas, temperatura confortável) são detalhes que custam quase nada mas influenciam diretamente a decisão emocional do comprador.

## O que NÃO vale a pena fazer

Renovações estruturais, cozinhas ou casas de banho completas, ou mudanças de pavimento raramente recuperam o investimento na venda — a menos que o estado atual seja realmente um impedimento à venda. Nestes casos, é normalmente mais eficiente ajustar o preço de venda do que investir em obras profundas.

## A minha recomendação prática

Antes de qualquer imóvel ir para o mercado, faço sempre uma visita de avaliação onde identifico, em conjunto com o proprietário, que intervenções de baixo custo vão ter mais impacto no valor percebido — e quais não compensam o investimento. É um trabalho de poucas horas que pode significar vender mais rápido e por um valor mais próximo do pedido.

---

**Quer uma avaliação gratuita do seu imóvel, com recomendações práticas para o preparar para venda?** Entre em contacto comigo — sem custos.
    `
  },
  {
    slug: '3-perguntas-fazer-ao-teu-consultor-imobiliario',
    titulo: 'As 3 Perguntas Que Tens de Fazer ao Teu Consultor Imobiliário',
    resumo: 'Escolher o consultor certo pode significar a diferença entre vender bem e rápido, ou ficar meses com o imóvel parado. Estas são as 3 perguntas que todo o proprietário deveria fazer antes de assinar um contrato de mediação.',
    data: '3 de Junho de 2026',
    leitura: '5 min',
    categoria: 'Vender',
    cor: '#0a3166',
    conteudo: `
## A escolha do consultor importa mais do que parece

Muitos proprietários escolhem consultor imobiliário com base em quem lhes prometeu o preço mais alto, ou em quem conheciam primeiro. É um erro caro. Estas são as 3 perguntas que recomendo fazer a qualquer consultor antes de assinar um contrato de mediação — e que respondo sempre com total transparência aos meus próprios clientes.

## Pergunta 1: "Como chegou a este valor para o meu imóvel?"

Esta é a pergunta mais importante de todas. Um consultor sério não dá um valor "de cabeça" ou baseado apenas no que o proprietário gostaria de ouvir.

**O que deve esperar como resposta:**
Um estudo comparativo de mercado, com imóveis semelhantes vendidos recentemente na mesma zona — não médias genéricas do concelho, mas transações reais e comparáveis em localização, tipologia e estado.

**Sinal de alerta:**
Se o consultor apresenta um valor significativamente mais alto do que os outros, sem justificação com dados concretos, é provavelmente uma tática para conseguir o contrato de mediação — não uma estimativa realista. Estes imóveis acabam, quase sempre, por baixar de preço meses depois, já com a imagem "queimada" no mercado.

## Pergunta 2: "Qual é exatamente a sua estratégia de promoção?"

Colocar o imóvel num portal e esperar não é estratégia — é esperança. Pergunte concretamente o que vai ser feito.

**O que deve esperar como resposta:**
Fotografia profissional, presença em portais relevantes, divulgação em redes sociais, acesso a uma base de compradores já qualificados, e, no caso de redes como a RE/MAX, distribuição através da rede de consultores e parceiros.

**Sinal de alerta:**
Respostas vagas como "vou divulgar o seu imóvel" sem detalhes concretos sobre canais, frequência ou método de qualificação de interessados.

## Pergunta 3: "O que acontece se o imóvel não vender no prazo previsto?"

Esta pergunta revela muito sobre a honestidade e profissionalismo do consultor.

**O que deve esperar como resposta:**
Uma explicação clara sobre o que será reavaliado — preço, fotografia, estratégia de divulgação — e disponibilidade para rever a abordagem com dados concretos sobre o feedback recebido nas visitas e contactos.

**Sinal de alerta:**
Se o consultor não tem um plano para esta situação, ou simplesmente diz "vai vender, não se preocupe", está a evitar uma conversa importante que deveria ter desde o início.

## Bónus: pergunte sempre pela exclusividade

Um contrato de mediação exclusivo, bem feito, normalmente traz melhores resultados do que ter o imóvel em várias agências em simultâneo — porque o consultor investe tempo e recursos reais, sabendo que tem o compromisso do proprietário. Pergunte ao seu consultor porque recomenda (ou não) a exclusividade, e exija que essa recomendação venha com argumentos, não apenas com interesse comercial.

## Porque faço questão de responder a estas 3 perguntas sempre

Antes de qualquer proprietário assinar comigo, explico sempre como cheguei ao valor proposto, qual é a estratégia concreta de promoção, e como vamos acompanhar e ajustar a estratégia se necessário. É este nível de transparência que, na minha experiência, gera confiança real — e resultados reais.

---

**Está a pensar vender e quer uma conversa transparente sobre o seu imóvel?** Faço uma avaliação gratuita e respondo a estas 3 perguntas (e a todas as outras que tiver) sem compromisso. Entre em contacto comigo.
    `
  },
  {
    slug: 't2-vs-t3-vs-t4-qual-vende-mais-rapido',
    titulo: 'T2 vs T3 vs T4: Qual Vende Mais Rápido na Zona Norte de Lisboa?',
    resumo: 'A tipologia do imóvel influencia diretamente o tempo de venda. Veja qual o perfil de comprador para cada tipologia e como isso afeta a estratégia de venda em Loures, VFX, Mafra, Alenquer e Benavente.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Vender',
    cor: '#0a3166',
    conteudo: `
## A tipologia não é só sobre tamanho — é sobre o comprador

Uma pergunta que recebo com frequência de proprietários é "qual tipologia vende mais rápido?" A resposta não é simples, porque cada tipologia atrai um perfil diferente de comprador, com motivações e urgências distintas. Entender isto é essencial para ajustar expectativas e estratégia.

## T2: o mais procurado, mas também o mais disputado pelo vendedor

Os T2 são tipicamente a tipologia com **maior número de compradores ativos** no mercado — casais jovens, investidores para arrendamento, e compradores a comprar a primeira casa.

**Perfil do comprador**: jovens casais, investidores, pessoas sozinhas com bom orçamento.

**Tempo médio de venda**: geralmente o mais rápido das três tipologias, sobretudo em zonas bem servidas por transportes como Vila Franca de Xira ou Loures.

**O que mais pesa na decisão**: localização, proximidade a transportes, e preço por m² competitivo face à concorrência direta.

## T3: o equilíbrio entre procura e oferta

O T3 é a tipologia mais **equilibrada** — há bastante procura, mas também há mais oferta no mercado, o que significa que a diferenciação do imóvel importa mais.

**Perfil do comprador**: famílias com 1 a 2 filhos, ou casais que planeiam ter filhos em breve.

**Tempo médio de venda**: intermédio — pode variar significativamente consoante o estado do imóvel e a apresentação.

**O que mais pesa na decisão**: número de quartos com boa dimensão, arrumação (despensas, roupeiros), e proximidade a escolas e serviços.

## T4: o mercado mais reduzido, mas com compradores mais decididos

Os T4 têm um mercado mais pequeno em número de compradores, mas quem procura este tipo de imóvel costuma ter já uma necessidade concreta e urgente — geralmente famílias maiores ou com necessidade de espaço extra (escritório, quarto de hóspedes).

**Perfil do comprador**: famílias maiores, frequentemente já donos de imóvel mais pequeno que precisam de mais espaço.

**Tempo médio de venda**: pode ser mais longo, mas quando há match com o comprador certo, o processo de decisão tende a ser mais rápido — estes compradores já sabem exatamente o que procuram.

**O que mais pesa na decisão**: espaço exterior (terraço, quintal), garagem para 2 carros, e divisão clara entre zona social e zona privada.

## Como isto varia entre os 5 concelhos

Em **Vila Franca de Xira e Loures**, com melhor acesso a Lisboa, os T2 e T3 têm procura mais intensa, impulsionada por quem trabalha na capital.

Em **Mafra**, a procura distribui-se mais por T3 e T4 com espaço exterior, refletindo um perfil de comprador que já privilegia qualidade de vida sobre proximidade absoluta.

Em **Alenquer e Benavente**, onde o mercado tem ciclo de venda mais longo, os T3 e T4 com terreno tendem a ser o produto mais distintivo e procurado — moradias com espaço exterior superam os apartamentos em interesse.

## O que isto significa para a sua estratégia de venda

Se tem um T2, a velocidade é normalmente do seu lado — mas isso não dispensa um preço correto desde o início, porque há muita oferta concorrente.

Se tem um T3, a apresentação e a diferenciação face a imóveis semelhantes fazem toda a diferença — é aqui que invisto mais tempo na preparação fotográfica e na narrativa do imóvel.

Se tem um T4, a paciência é mais importante — mas a qualificação dos interessados (confirmar que têm mesmo necessidade e capacidade para aquele imóvel) evita visitas inúteis e acelera o processo real.

---

**Quer saber como a sua tipologia específica está a comportar-se no mercado atual?** Faço uma análise comparativa gratuita para o seu imóvel. Entre em contacto comigo.
    `
  },
  {
    slug: 'quanto-demora-vender-casa-concelho',
    titulo: 'Quanto Demora Vender uma Casa? O Que os Números Dizem em 2026',
    resumo: 'O tempo de venda varia muito entre concelhos e tipos de imóvel. Veja os prazos médios reais em Loures, Vila Franca de Xira, Mafra, Alenquer e Benavente — e os fatores que mais aceleram ou atrasam uma venda.',
    data: '3 de Junho de 2026',
    leitura: '6 min',
    categoria: 'Vender',
    cor: '#d80c21',
    conteudo: `
## A pergunta que todo o vendedor faz primeiro

"Quanto tempo vai demorar a vender a minha casa?" É quase sempre a primeira pergunta numa avaliação. A resposta honesta é: depende do concelho, da tipologia, do estado do imóvel e, sobretudo, do preço de entrada. Mas há dados concretos que ajudam a definir expectativas realistas.

## Os prazos médios por concelho em 2026

**Vila Franca de Xira**: entre 45 e 90 dias, beneficiando da ligação ferroviária direta a Lisboa em 20 minutos e da forte procura por parte de quem trabalha na capital.

**Loures**: entre 45 e 75 dias, um dos mercados mais dinâmicos da região, com procura consistente sobretudo em Sacavém, Prior Velho e Portela.

**Mafra**: entre 60 e 100 dias, com a Ericeira a funcionar como motor de procura adicional, mas com um mercado globalmente mais sazonal.

**Alenquer**: entre 60 e 100 dias, mercado em consolidação, com potencial de valorização mas ciclo de venda mais longo.

**Benavente**: entre 60 e 120 dias, o concelho com ciclo mais longo dos cinco, refletindo um mercado ainda em fase de crescimento da procura.

## Os 4 fatores que mais influenciam o tempo de venda

**1. O preço de entrada**
Este é, sem comparação, o fator mais determinante. Um imóvel com preço correto desde o primeiro dia recebe mais visitas nas primeiras 2 a 3 semanas — período crítico em que o interesse é maior. Um imóvel sobrevalorizado perde esse momento e acaba, quase sempre, por vender por menos tempo depois, já com a imagem "queimada".

**2. A qualidade da apresentação**
Fotografia profissional, casa preparada e arrumada, e descrição bem escrita podem reduzir o tempo de venda em semanas, sobretudo nas primeiras fases de divulgação online.

**3. A exposição do imóvel**
Um imóvel presente apenas num portal, sem promoção ativa em redes sociais ou rede de contactos, demora naturalmente mais tempo a encontrar o comprador certo.

**4. A época do ano**
Embora menos determinante do que os fatores anteriores, o mercado tende a ter mais atividade na primavera e no início do outono, com alguma desaceleração no verão e no período de festas.

## O que fazer se o seu imóvel já está parado há meses

Se o imóvel está no mercado há mais tempo do que a média do concelho sem propostas sérias, normalmente um destes três fatores está em causa:

→ O preço está desalinhado com o que o mercado está disposto a pagar

→ A apresentação (fotografia, descrição) não está a gerar interesse suficiente

→ A exposição do imóvel é insuficiente — está pouco visível para quem procura

Nestes casos, a solução raramente é "esperar mais" — é reavaliar com dados atualizados e ajustar a estratégia.

## Como acompanho este processo com os meus clientes

Defino sempre, desde o início, marcos de avaliação — por exemplo, às 3 e às 6 semanas — para analisar o feedback real das visitas e contactos, e ajustar o que for necessário antes que o tempo no mercado se torne ele próprio um problema.

---

**Quer saber o prazo realista para vender o seu imóvel na sua zona específica?** Faço uma avaliação gratuita com uma estimativa honesta de tempo de venda. Entre em contacto comigo.
    `
  },
  {
    slug: 'vender-casa-inquilino-lei-portuguesa-2026',
    titulo: 'Vender Casa com Inquilino: O Que Diz a Lei Portuguesa em 2026',
    resumo: 'Vender um imóvel arrendado é mais comum do que parece, mas envolve regras legais específicas. Saiba o que pode e não pode fazer, como notificar o inquilino, e como gerir o direito de preferência sem complicações.',
    data: '3 de Junho de 2026',
    leitura: '7 min',
    categoria: 'Vender',
    cor: '#0a3166',
    conteudo: `
## Vender com inquilino é possível — mas com regras claras

Ter uma casa arrendada e querer vendê-la é uma situação comum, mas mais complexa do que muitos proprietários antecipam. A lei portuguesa protege de forma clara os direitos do inquilino, e desconhecer estas regras pode atrasar a venda ou, em casos mais graves, gerar litígios que comprometem todo o negócio.

## O inquilino pode impedir a venda?

**Não.** O inquilino não tem poder para impedir a venda de um imóvel arrendado. O que a lei lhe confere é o **direito de preferência** — a possibilidade de comprar o imóvel em igualdade de condições com qualquer outro comprador, se cumprir os requisitos legais.

## Quem tem direito de preferência

Segundo o artigo 1091.º do Código Civil (alterado pela Lei n.º 64/2018), o inquilino tem direito de preferência se cumprir, cumulativamente:

→ O arrendamento ser para fins habitacionais (em determinados casos aplica-se também a não habitacional)

→ O contrato estar em vigor há **mais de 2 anos**

→ A compra ser destinada a habitação própria do inquilino — se a intenção for arrendar a terceiros ou alojamento local, a preferência não se aplica

## Como notificar o inquilino corretamente

Antes de vender a terceiros, o proprietário deve informar o inquilino, por **carta registada com aviso de receção**, da intenção de venda, incluindo:

- O preço acordado
- As condições de pagamento
- Restantes termos do negócio

O inquilino tem então um prazo legal — entre 8 dias úteis e 30 dias, dependendo da fonte e do tipo de situação, por isso confirme sempre o prazo aplicável ao seu caso concreto — para decidir se quer exercer a preferência.

## O que acontece se o inquilino não responder ou recusar

Se o inquilino não exercer o direito dentro do prazo, ou declarar expressamente que não tem interesse, o proprietário pode avançar livremente com a venda a terceiros, sem mais formalidades relacionadas com este direito.

## E se o proprietário vender sem notificar o inquilino?

Esta é a situação mais arriscada. Se o direito de preferência não for respeitado, o inquilino pode intentar uma **ação judicial de preferência**, com o objetivo de adquirir o imóvel pelas mesmas condições da venda já realizada. Isto pode anular ou complicar seriamente uma venda já concluída — incluindo casos em que o novo proprietário já pagou e tomou posse.

## O que acontece ao contrato de arrendamento após a venda

O contrato de arrendamento **transfere-se automaticamente** para o novo proprietário, nas mesmas condições — valor de renda, prazo, e demais termos acordados. O novo proprietário não pode expulsar o inquilino nem alterar unilateralmente o contrato existente. Este processo de transferência é automático, sem necessidade de novo contrato, salvo se ambas as partes quiserem renegociar.

## O inquilino pode dificultar o processo de venda?

Embora não possa impedir a venda, um inquilino pouco cooperativo pode recusar visitas ou dificultar o acesso ao imóvel durante o processo. Isto não invalida a venda, mas pode tornar o processo mais lento e exigir maior diplomacia na gestão da relação entre proprietário, inquilino e potenciais compradores.

## A minha recomendação prática

Sempre que um cliente me traz um imóvel arrendado para venda, o primeiro passo é confirmar se o inquilino tem ou não direito de preferência, e organizar a notificação formal antes de qualquer divulgação pública do imóvel. Esta sequência evita atrasos e protege o vendedor de complicações legais que, na prática, custam tempo e dinheiro a todos.

---

**Tem um imóvel arrendado e quer saber como vendê-lo corretamente?** Posso ajudá-lo a navegar todo o processo, incluindo a relação com o inquilino. Entre em contacto comigo.
    `
  },
  {
    slug: 'investir-imobiliario-zona-norte-lisboa-5-razoes',
    titulo: 'Investir em Imobiliário na Zona Norte de Lisboa: 5 Razões',
    resumo: 'Loures, Vila Franca de Xira, Mafra, Alenquer e Benavente formam uma das regiões com maior potencial de investimento imobiliário da Área Metropolitana de Lisboa. Veja as 5 razões que tornam esta zona uma aposta inteligente em 2026.',
    data: '3 de Junho de 2026',
    leitura: '7 min',
    categoria: 'Investir',
    cor: '#051d40',
    conteudo: `
## Uma região, cinco mercados, uma tendência comum

Loures, Vila Franca de Xira, Mafra, Alenquer e Benavente formam um corredor que, apesar das diferenças entre si, partilha uma característica comum: continuam a oferecer melhor relação qualidade-preço do que Lisboa e os concelhos já totalmente consolidados da Grande Lisboa. Eis as 5 razões que tornam esta região uma aposta a considerar seriamente em 2026.

## Razão 1: Preços ainda competitivos com margem de valorização

Enquanto Lisboa se aproxima dos **5.200€/m²** e concelhos como Loures e Mafra já ultrapassam os **4.400€/m²**, Alenquer e Benavente mantêm-se entre **1.400€ e 2.400€/m²** — um diferencial que cria espaço real para valorização nos próximos anos, à medida que a procura se desloca progressivamente para fora de Lisboa.

## Razão 2: Acessibilidade reforçada a Lisboa

Toda a região beneficia de ligações diretas à capital: a linha ferroviária de Azambuja conecta Vila Franca de Xira e Alverca ao centro de Lisboa em cerca de 20-30 minutos, as autoestradas A1, A8, A10 e A21 garantem mobilidade rodoviária rápida, e a proximidade ao Aeroporto Humberto Delgado beneficia diretamente Loures e Vila Franca de Xira.

## Razão 3: Procura estrutural por mais espaço pelo mesmo orçamento

Com os preços em Lisboa a tornarem-se inacessíveis para muitas famílias, há uma procura estrutural — não passageira — por concelhos limítrofes que oferecem mais espaço, frequentemente com jardim ou terreno, pelo mesmo orçamento que em Lisboa compraria um apartamento pequeno sem exterior.

## Razão 4: Rentabilidade de arrendamento acima da média

Para quem compra para arrendar, as rendas médias de um T2 em Alverca ou Póvoa de Santa Iria, por exemplo, variam entre **800€ e 1.100€/mês**, o que face aos preços de aquisição da zona representa uma rentabilidade bruta de **4% a 6%** — claramente acima da generalidade dos depósitos a prazo e de muitos outros instrumentos de investimento de baixo risco.

## Razão 5: Diversidade de perfis dentro da mesma região

Esta região não é homogénea, e isso é uma vantagem para o investidor: Vila Franca de Xira e Loures oferecem mercados mais líquidos e dinâmicos, ideais para quem procura rotatividade mais rápida; Mafra junta o potencial costeiro da Ericeira a zonas mais tradicionais; Alenquer e Benavente oferecem entrada a preços mais baixos com potencial de valorização a médio-longo prazo, ideais para investidores com horizonte temporal mais alargado.

## Os riscos que deve considerar

Nenhum investimento está isento de risco. Mercados como Alenquer e Benavente têm menor liquidez na revenda e ciclos de venda mais longos. A gestão de arrendamento exige tempo ou um intermediário de confiança. E a valorização, embora provável dada a tendência regional, não é garantida.

## Como escolho onde investir com cada cliente

Não existe uma resposta única para "onde investir" — depende do horizonte temporal, do capital disponível, e do objetivo (rentabilidade de arrendamento vs. valorização de capital vs. uso próprio futuro). Faço sempre uma análise personalizada cruzando estes fatores com os dados reais de cada concelho e freguesia.

---

**Está a considerar investir em imobiliário na zona norte de Lisboa?** Faço uma análise personalizada à sua situação e objetivos, com dados reais da região. Entre em contacto comigo — sem custos.
    `
  },
];

export const ARTIGOS = [
  ...ARTIGOS_SEMANAIS,
  ...ARTIGOS_BASE.filter((a) => !ARTIGOS_SEMANAIS.some((n) => n.slug === a.slug)),
];

export default function BlogPage() {
  return (
    <div className="blog-page">
      <Navbar />

      <div className="blog-hero">
        <div className="container">
          <span className="section-label">Conhecimento</span>
          <h1>Blog Imobiliário</h1>
          <p>Artigos práticos e actualizados sobre compra, venda e investimento imobiliário.</p>
        </div>
      </div>

      <div className="blog-body">
        <div className="container">
          <div className="artigos-grid">
            {ARTIGOS.map(a => (
              <Link to={`/blog/${a.slug}`} className="artigo-card" key={a.slug}>
                <div className="artigo-card-top" style={{ background: a.cor }}>
                  <span className="artigo-categoria">{a.categoria}</span>
                  <span className="artigo-leitura">⏱ {a.leitura} de leitura</span>
                </div>
                <div className="artigo-card-body">
                  <p className="artigo-data">{a.data}</p>
                  <h2>{a.titulo}</h2>
                  <p className="artigo-resumo">{a.resumo}</p>
                  <span className="artigo-ler">Ler artigo →</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="blog-cta">
            <h3>Tem alguma dúvida sobre imobiliário?</h3>
            <p>Fale directamente comigo — respondo a todas as questões.</p>
            <a href="https://wa.me/351969692793?text=Olá%20Marco,%20li%20o%20seu%20blog%20e%20tenho%20uma%20questão." target="_blank" rel="noopener noreferrer">
              💬 Perguntar no WhatsApp
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
